import { useEffect, useMemo } from 'react';

import { baseVars } from '../config-vars';
import { layoutClasses } from '../classes';
import { cn } from '@/lib/utils';

// ----------------------------------------------------------------------

export type LayoutSectionProps = {
  className?: string;
  cssVars?: Record<string, string | number>;
  children?: React.ReactNode;
  footerSection?: React.ReactNode;
  headerSection?: React.ReactNode;
  sidebarSection?: React.ReactNode;
};

export function LayoutSection({
  className,
  cssVars,
  children,
  footerSection,
  headerSection,
  sidebarSection,
}: LayoutSectionProps) {
  const allVars = useMemo(
    () => {
      const vars: Record<string, string> = {};
      for (const [key, value] of Object.entries({ ...baseVars, ...cssVars })) {
        vars[key] = String(value);
      }
      return vars;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(cssVars)],
  );

  // Set non-layout vars on body for global access (z-index, colors, etc.)
  useEffect(() => {
    const body = document.body;
    const globalVars = Object.entries(allVars).filter(
      ([key]) => !key.includes('--layout-nav-vertical-width'),
    );
    globalVars.forEach(([key, value]) => {
      body.style.setProperty(key, value);
    });
    return () => {
      globalVars.forEach(([key]) => {
        body.style.removeProperty(key);
      });
    };
  }, [allVars]);

  return (
    <div
      id="root__layout"
      className={cn(layoutClasses.root, className)}
      style={allVars as React.CSSProperties}
    >
      {sidebarSection}
      <div
        className={cn(
          layoutClasses.hasSidebar,
          'flex min-h-screen flex-1 flex-col bg-background md:ml-[var(--layout-nav-vertical-width)] md:transition-[margin-left,--layout-nav-vertical-width] md:duration-200 md:ease-[cubic-bezier(0.4,0,0.2,1)] md:will-change-[margin-left]',
        )}
      >
        {headerSection}
        {children}
        {footerSection}
      </div>
    </div>
  );
}
